/**
 * The manifest a publisher serves to prove where they should be paid.
 *
 * ── Why this lives in a shared package ───────────────────────────────────
 *
 * The API verifies the signature; the app asks a wallet to produce it. Those
 * are two codebases building the same string, and if they ever disagree by a
 * single character every signature the app produces fails verification — with
 * no useful error, because "signature does not match" is what a forgery looks
 * like too. One definition, imported by both, is the only arrangement where
 * that class of bug cannot exist.
 *
 * ── The security property ────────────────────────────────────────────────
 *
 * Two proofs in one file. Hosting it at your own origin proves you control the
 * domain; signing it proves you hold the key that will be paid. Neither alone
 * is enough: a file with no signature could name any address, and a signature
 * with no file could be lifted from anywhere.
 *
 * What makes the second half work is that the verifier REBUILDS this message
 * from the hostname it fetched the file from — never from the file's contents.
 * A manifest is public by construction, so its signature is public too and can
 * be copied onto any other domain; copied, it verifies against a message naming
 * the copier, recovers an unrelated address, and fails.
 */

/** Where a publisher serves it. */
export const MANIFEST_PATH = "/.well-known/adland.json";

/** Named in the message, so a signature made for another dapp cannot be reused. */
export const SIGNING_DOMAIN = "adland.space";

/**
 * The chain named in the message. Only meaningful for a smart account, whose
 * signature is validated by contract code living on exactly one chain, but it
 * is signed unconditionally so one message shape covers both account types.
 */
export const SIGNING_CHAIN_ID = 8453;

export type PublisherManifest = {
  version: number;
  payTo: string;
  nonce: string;
  issuedAt: string;
  signature: string;
  /** Informational, and deliberately not trusted by the verifier. */
  domain?: string;
  /**
   * How to reach this publisher. Optional, and outside the signature.
   *
   * A URI with a scheme, so it says what it is without a second field to
   * disagree with: `mailto:you@example.com` or `https://t.me/handle`. Anything
   * else is refused rather than stored — a bare handle is ambiguous between
   * three services and a bare address is a mail client's guess.
   *
   * Not signed, and it does not need to be. The signature exists to bind a
   * PAYOUT ADDRESS to a domain, which is the only claim here that moves money.
   * Everything else in the file rides on domain control alone, which is exactly
   * the right strength for "here is where to email me": only the site's owner
   * can put it there, and getting it wrong costs them a message rather than a
   * payment.
   */
  contact?: string;
};

/** Schemes a publisher may leave a contact under. */
export const CONTACT_SCHEMES = ["mailto:", "https://t.me/"] as const;

/**
 * Normalise a contact, or null if it is not one we will store.
 *
 * Deliberately narrow. An open `https://` field is a link we would render
 * beside a publisher's name on an admin screen, which is a small phishing
 * surface for no benefit; two known schemes cover the ways anybody has actually
 * asked to be reached.
 */
export function parseContact(input: unknown): string | null {
  if (typeof input !== "string") return null;
  const value = input.trim();
  if (value.length === 0 || value.length > 200) return null;
  if (!CONTACT_SCHEMES.some((scheme) => value.startsWith(scheme))) return null;
  // No whitespace, and nothing that could break out of an href.
  if (/[\s<>"']/.test(value)) return null;
  return value;
}

/**
 * The exact bytes a publisher signs, in EIP-4361 (SIWE) layout.
 *
 * SIWE rather than a bespoke string for one practical reason: wallets recognise
 * the shape and render it as structured text with the address and origin called
 * out, instead of as a hex blob nobody can evaluate. Someone authorising a
 * payout address should be able to read what they are authorising.
 *
 * Every line is load-bearing. `domain` makes the signature useless anywhere
 * else; `payTo` is the thing being authorised; the first line scopes it to us,
 * so a login signature made for another site cannot be submitted here; the
 * nonce and date allow rotation and expiry.
 */
export function manifestMessage(params: {
  domain: string;
  payTo: string;
  nonce: string;
  issuedAt: string;
}): string {
  const { domain, payTo, nonce, issuedAt } = params;
  return [
    `${SIGNING_DOMAIN} wants you to sign in with your Ethereum account:`,
    payTo,
    "",
    `Authorise adland to pay this address for ads served on ${domain}.`,
    "",
    `URI: https://${domain}`,
    "Version: 1",
    `Chain ID: ${SIGNING_CHAIN_ID}`,
    `Nonce: ${nonce}`,
    `Issued At: ${issuedAt}`,
  ].join("\n");
}

/** Reject a shape before spending a signature check on it. */
export function parseManifest(input: unknown): PublisherManifest | null {
  if (!input || typeof input !== "object") return null;
  const m = input as Record<string, unknown>;
  if (m.version !== 1) return null;
  for (const key of ["payTo", "nonce", "issuedAt", "signature"] as const) {
    if (typeof m[key] !== "string" || !(m[key] as string).trim()) return null;
  }
  return {
    version: 1,
    payTo: (m.payTo as string).trim(),
    nonce: (m.nonce as string).trim(),
    issuedAt: (m.issuedAt as string).trim(),
    signature: (m.signature as string).trim(),
    domain: typeof m.domain === "string" ? m.domain.trim() : undefined,
    contact: parseContact(m.contact) ?? undefined,
  };
}

/**
 * A nonce for a fresh manifest.
 *
 * Only has to be unique enough that re-signing produces a different message, so
 * an old file cannot be mistaken for a new one. Not a security boundary — the
 * domain binding is — so `crypto.getRandomValues` where it exists and a
 * timestamp where it does not is ample.
 */
export function manifestNonce(): string {
  const g = globalThis as {
    crypto?: { getRandomValues?: typeof crypto.getRandomValues };
  };
  if (g.crypto?.getRandomValues) {
    const bytes = new Uint8Array(8);
    g.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  return Date.now().toString(36);
}
